import React, { useEffect, useState, useRef } from "react";
import "./contactus.css";
import { useGeneralSetting } from "../../context/GeneralSettingContext";
import ReCAPTCHA from "react-google-recaptcha";
import { useDispatch, useSelector } from "react-redux";
import {
  submitEnquiryForm,
  resetEnquiryFormState,
} from "../../store/slices/enquiryFormSlice";

const ContactUs = () => {
  const { generalSetting } = useGeneralSetting();
  const dispatch = useDispatch();
  const recaptchaRef = useRef(null);
  const [captchaValue, setCaptchaValue] = useState(null);

  const [services, setServices] = useState([]);
  const [subServicesMap, setSubServicesMap] = useState({});
  const [loadingServices, setLoadingServices] = useState(false);
  const [servicesError, setServicesError] = useState("");

  const [isSecondEmailOpen, setIsSecondEmailOpen] = useState(false);
  const [isSecondMobileOpen, setIsSecondMobileOpen] = useState(false);
  const [hasAgreedTerms, setHasAgreedTerms] = useState(false);

  const [submitMessage, setSubmitMessage] = useState("");
  const [submitStatus, setSubmitStatus] = useState("");

  const { loading, success } = useSelector((state) => state.enquiryForm || {});

  const [contactForm, setContactForm] = useState({
    fullName: "",
    panNumber: "",
    email: "",
    mobile: "",
    ourServices: "", // main service name
    subServiceId: "", // selected sub-service ID (the only thing we store)
    referenceName: "",
    referenceEmail: "",
    secondaryEmail: "",
    secondaryMobile: "",
  });

  // Fetch services
  useEffect(() => {
    const fetchSubServices = async () => {
      setLoadingServices(true);
      try {
        const res = await fetch("https://loan-house.com/api/v1/sub-services");
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) {
          const grouped = {};
          const uniqueServices = [];
          data.data.forEach((item) => {
            const service = item.service;
            if (service && !uniqueServices.some((s) => s.id === service.id)) {
              uniqueServices.push({ id: service.id, name: service.name });
            }
            if (!grouped[service.id]) grouped[service.id] = [];
            grouped[service.id].push({ id: item.id, name: item.name });
          });
          setServices(uniqueServices);
          setSubServicesMap(grouped);
        }
      } catch (err) {
        setServicesError("Failed to load services");
      } finally {
        setLoadingServices(false);
      }
    };
    fetchSubServices();
  }, []);

  // Auto-clear message
  useEffect(() => {
    if (!submitMessage) return;
    const timer = setTimeout(() => {
      setSubmitMessage("");
      setSubmitStatus("");
    }, 3000);
    return () => clearTimeout(timer);
  }, [submitMessage]);

  // Reset after success
  useEffect(() => {
    if (success) {
      setSubmitMessage("Form submitted successfully ✅");
      setSubmitStatus("success");
      setContactForm({
        fullName: "",
        panNumber: "",
        email: "",
        mobile: "",
        ourServices: "",
        subServiceId: "",
        referenceName: "",
        referenceEmail: "",
        secondaryEmail: "",
        secondaryMobile: "",
      });
      setHasAgreedTerms(false);
      setIsSecondEmailOpen(false);
      setIsSecondMobileOpen(false);
      setCaptchaValue(null);
      if (recaptchaRef.current) recaptchaRef.current.reset();
      dispatch(resetEnquiryFormState());
    }
  }, [success, dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "ourServices" && { subServiceId: "" }), // reset sub when main service changes
    }));
  };

  // Helper: get sub-service name from ID
  const getSubServiceName = (serviceName, subId) => {
    if (!subId || !serviceName) return "";
    const selectedService = services.find((s) => s.name === serviceName);
    if (!selectedService) return "";
    const subList = subServicesMap[selectedService.id] || [];
    const found = subList.find((sub) => sub.id === subId);
    return found ? found.name : "";
  };

  // Validation – now checks subServiceId instead of name
  const validateForm = () => {
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
    const phoneRegex = /^[0-9]{10}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!contactForm.fullName) return "Full Name is required";
    if (!contactForm.email) return "Email is required";
    if (!contactForm.mobile) return "Mobile is required";
    if (!contactForm.ourServices) return "Please select a service";
    if (!contactForm.subServiceId) return "Please select a service type";

    if (
      contactForm.panNumber &&
      !panRegex.test(contactForm.panNumber.toUpperCase())
    ) {
      return "Please enter a valid PAN number (ABCDE1234F)";
    }
    if (!phoneRegex.test(contactForm.mobile)) {
      return "Please enter a valid 10-digit mobile number";
    }
    if (
      isSecondMobileOpen &&
      contactForm.secondaryMobile &&
      !phoneRegex.test(contactForm.secondaryMobile)
    ) {
      return "Please enter a valid alternate mobile number";
    }
    if (!emailRegex.test(contactForm.email)) {
      return "Please enter a valid email address";
    }
    if (
      isSecondEmailOpen &&
      contactForm.secondaryEmail &&
      !emailRegex.test(contactForm.secondaryEmail)
    ) {
      return "Please enter a valid alternate email address";
    }
    if (
      contactForm.email &&
      contactForm.secondaryEmail &&
      contactForm.email === contactForm.secondaryEmail
    ) {
      return "Primary and secondary email addresses cannot be the same";
    }
    if (
      contactForm.mobile &&
      contactForm.secondaryMobile &&
      contactForm.mobile === contactForm.secondaryMobile
    ) {
      return "Primary and secondary mobile numbers cannot be the same";
    }
    return null;
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const error = validateForm();
    if (error) {
      setSubmitMessage(error);
      setSubmitStatus("error");
      return;
    }
    if (!captchaValue) {
      setSubmitMessage("Please verify that you are human");
      setSubmitStatus("error");
      return;
    }

    // Get sub-service name from ID
    const subServiceName = getSubServiceName(
      contactForm.ourServices,
      contactForm.subServiceId,
    );

    const payload = {
      fullName: contactForm.fullName.trim(),
      panNumber: contactForm.panNumber
        ? contactForm.panNumber.toUpperCase().trim()
        : "",
      email: contactForm.email.trim(),
      mobile: contactForm.mobile.trim(),
      ourServices: contactForm.ourServices,
      subService: subServiceName, // send NAME
      referenceName: contactForm.referenceName || "",
      referenceEmail: contactForm.referenceEmail || "",
      agreeToUpdates: hasAgreedTerms,
    };

    console.log("🚀 Payload with Sub-Service NAME:", payload);
    dispatch(submitEnquiryForm(payload));
  };

  return (
    <div className="contactus-container">
      <div className="contactus-header">
        <h1>Have any query? Get in Touch.</h1>
        <p>Feel free to reach out with any questions or let's collaborate.</p>
      </div>

      <div className="contactus-query">
        <div className="contactus-leftside">
          <div
            className="contact-card call"
            onClick={() =>
              window.open(
                `https://wa.me/${generalSetting?.whatsapp_number || "9909002220"}?text=Hello, I would like to know more about your services.`,
                "_blank",
              )
            }
          >
            <div className="icon">📞</div>
            <div className="content">
              <h6>Call us on</h6>
              <h4>+91 {generalSetting?.contact_number || "9909002220"}</h4>
            </div>
          </div>
          <div
            className="contact-card mail"
            onClick={() =>
              window.open(
                `mailto:${generalSetting?.email || "info@loan-house.com"}?subject=Inquiry&body=Hello, I would like to know more about your services.`,
                "_blank",
              )
            }
          >
            <div className="icon">📨</div>
            <div className="content">
              <h6>Mail us</h6>
              <h4>{generalSetting?.email || "info@loan-house.com"}</h4>
            </div>
          </div>
          <div className="contact-card join">
            <div className="icon">🚀</div>
            <div className="content">
              <h4>Address!</h4>
              <p>
                LOAN HOUSE HARISH NAGAR PLOT NO 1-2 NEAR GREEN ENCLAVE BESSIE,
                TAPI RIVER ROAD, Gujarat Gas Cir, Anand Vatika Society, Harish
                Nagar Society, Adajan, Surat, Gujarat 395009
              </p>
            </div>
          </div>
        </div>

        <div className="contactus-rightside">
          <div className="contact-form-container">
            {submitMessage && (
              <div className={`submit-message ${submitStatus}`}>
                {submitMessage}
                <button
                  className="close-message-btn"
                  onClick={() => {
                    setSubmitMessage("");
                    setSubmitStatus("");
                  }}
                >
                  ×
                </button>
              </div>
            )}
            <form className="contact-form" onSubmit={handleContactSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>👤 Full Name *</label>
                  <input
                    name="fullName"
                    value={contactForm.fullName}
                    onChange={handleInputChange}
                    placeholder="Enter full name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>📄 PAN Number</label>
                  <input
                    name="panNumber"
                    value={contactForm.panNumber}
                    onChange={handleInputChange}
                    maxLength="10"
                    placeholder="Enter PAN Number"
                    style={{ textTransform: "uppercase" }}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <div className="second-option-parent">
                    <label>
                      <span className="field-emoji">✉️</span> E-mail *
                    </label>
                    <div className="second-option">
                      <div
                        className="button"
                        onClick={() => {
                          setIsSecondEmailOpen(!isSecondEmailOpen);
                          setContactForm((prev) => ({
                            ...prev,
                            secondaryEmail: "",
                          }));
                        }}
                      >
                        {!isSecondEmailOpen ? "+" : "-"}
                      </div>
                    </div>
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={contactForm.email}
                    onChange={handleInputChange}
                    placeholder="loansviakrishna@gmail.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <div className="second-option-parent">
                    <label>
                      <span className="field-emoji">📱</span> Mobile *
                    </label>
                    <div className="second-option">
                      <div
                        className="button"
                        onClick={() => {
                          setIsSecondMobileOpen(!isSecondMobileOpen);
                          setContactForm((prev) => ({
                            ...prev,
                            secondaryMobile: "",
                          }));
                        }}
                      >
                        {!isSecondMobileOpen ? "+" : "-"}
                      </div>
                    </div>
                  </div>
                  <input
                    type="tel"
                    name="mobile"
                    value={contactForm.mobile}
                    onChange={handleInputChange}
                    placeholder="9737304440"
                    required
                    pattern="[0-9]{10}"
                    title="Enter 10-digit mobile number"
                  />
                </div>
              </div>

              <div className="form-row">
                {isSecondEmailOpen && (
                  <div className="form-group">
                    <label>
                      <span className="field-emoji">✉️</span> Alternate E-mail
                    </label>
                    <input
                      type="email"
                      name="secondaryEmail"
                      value={contactForm.secondaryEmail}
                      onChange={handleInputChange}
                      placeholder="Alternate email"
                    />
                  </div>
                )}
                {isSecondMobileOpen && (
                  <div className="form-group">
                    <label>
                      <span className="field-emoji">📱</span> Alternate Mobile
                    </label>
                    <input
                      type="tel"
                      name="secondaryMobile"
                      value={contactForm.secondaryMobile}
                      onChange={handleInputChange}
                      placeholder="Alternate mobile"
                      pattern="[0-9]{10}"
                    />
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>📋 Our Services *</label>
                  {loadingServices ? (
                    <div className="loading-select">Loading services...</div>
                  ) : servicesError ? (
                    <div className="error-select">{servicesError}</div>
                  ) : (
                    <select
                      className="loan-type-select"
                      name="ourServices"
                      value={contactForm.ourServices}
                      onChange={handleInputChange}
                    >
                      <option value="">-- Select --</option>
                      {services.map((service) => (
                        <option key={service.id} value={service.name}>
                          {service.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="form-group">
                  <label>📊 Service Type *</label>
                  <select
                    className="loan-type-select"
                    name="subServiceId"
                    value={contactForm.subServiceId}
                    onChange={(e) =>
                      setContactForm((prev) => ({
                        ...prev,
                        subServiceId: e.target.value,
                      }))
                    }
                    disabled={!contactForm.ourServices}
                  >
                    <option value=""> -- Select -- </option>
                    {contactForm.ourServices &&
                      (() => {
                        const selected = services.find(
                          (s) => s.name === contactForm.ourServices,
                        );
                        const subList = selected
                          ? subServicesMap[selected.id] || []
                          : [];
                        return subList.map((sub) => (
                          <option key={sub.id} value={sub.id}>
                            {sub.name}
                          </option>
                        ));
                      })()}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>
                    <span className="field-emoji">👤</span> Reference Name
                  </label>
                  <input
                    type="text"
                    name="referenceName"
                    value={contactForm.referenceName}
                    onChange={handleInputChange}
                    placeholder="Enter reference name"
                  />
                </div>
                <div className="form-group">
                  <label>
                    <span className="field-emoji">📄</span> Reference E-mail
                  </label>
                  <input
                    type="email"
                    name="referenceEmail"
                    value={contactForm.referenceEmail}
                    onChange={handleInputChange}
                    placeholder="reference@example.com"
                  />
                </div>
              </div>

              <div className="li-terms-group">
                <input
                  type="checkbox"
                  checked={hasAgreedTerms}
                  onChange={(e) => setHasAgreedTerms(e.target.checked)}
                />
                <label>
                  I agree to receive loan updates and promotional from{" "}
                  <strong>Krishana Finconcept</strong> via WhatsApp and other
                  accept the{" "}
                  <a
                    href="/privacy-security-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms & Conditions
                  </a>
                </label>
              </div>

              <div className="recaptcha-box">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA}
                  onChange={(value) => setCaptchaValue(value)}
                />
              </div>

              <button
                className="submit-btn"
                disabled={!hasAgreedTerms || loading}
              >
                {loading ? "Submitting..." : "Submit"}
              </button>
            </form>
          </div>
        </div>
      </div>

      <div className="contactus-map">
        <iframe
          src={generalSetting?.google_map || ""}
          width="100%"
          height="450"
          allowFullScreen
          style={{ border: 0 }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Office location"
        ></iframe>
      </div>
    </div>
  );
};

export default ContactUs;
